import {React,useEffect,useState} from 'react';
import { Button, Typography, Box, Paper, Radio, RadioGroup, FormControlLabel, useMediaQuery, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

export default function RouteSelection({
    routeType,
    onRouteTypeChange,
    onRouteSchedulesClick,
    onAssignSchedulesClick,
    isUpdateMode = false
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    // Add state for user features
    const [userFeatures, setUserFeatures] = useState([]);

    // Load features from localStorage on component mount
    useEffect(() => {
        try {
            const localDataString = localStorage.getItem('userState');
            if (localDataString) {
                const localData = JSON.parse(localDataString);
                if (localData && localData.features) {
                    setUserFeatures(localData.features);
                }
            }
        } catch (error) {
            console.error('Error loading features from localStorage:', error);
        }
    }, []);

    // Permission check function
    const hasPermission = (apiPath, action) => {
        return userFeatures.some(feature =>
            feature.api_path === apiPath && feature.action === action
        );
    };

    // Check for schedule permissions
    const canViewSchedules = hasPermission('/api/schedule/route', 'GET'); // For Route Schedules button
    const canAssignSchedules = hasPermission('/api/schedule/vehicle', 'PUT'); // For Assign Schedules button

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'space-between',
            mb: 2,
            px: { xs: 2, sm: 3, md: 5 },
            gap: 2,
        }}>
            {/* Radio Button Section */}
            <Paper
                elevation={2}
                sx={{
                    p: { xs: 1, sm: 2 },
                    width: '100%',
                    borderRadius: '8px',
                    borderWidth: '1px',
                    border: "1px solid #A5DCF3",
                    boxShadow: "-2px 2px 5px 0px #00000040",
                    padding: { xs: '10px 14px', sm: '6px 20px' },
                    display: 'flex',
                    flexDirection: isSmall ? 'column' : 'row',
                    alignItems: isSmall ? 'flex-start' : 'center',
                    gap: isSmall ? 1 : 2
                }}
            >
                {/* Container for equal spacing with left alignment */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: isSmall ? '1fr' : 'repeat(3, 1fr)',
                        width: '100%',
                        alignItems: 'center',
                    }}
                >
                    {/* Label in first column */}
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 600,
                            textAlign: 'left',
                            color: '#183059',
                            fontFamily: 'Roboto',
                            lineHeight: '100%',
                            letterSpacing: 0,
                        }}
                    >
                        {t("Route Type")}:
                    </Typography>

                    {/* Radio buttons in their own columns but with left alignment */}
                    <RadioGroup
                        row={!isSmall}
                        value={routeType}
                        onChange={(e) => onRouteTypeChange(e.target.value)}
                        sx={{
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: isSmall ? '1fr' : 'repeat(2, 1fr)',
                            gridColumn: isSmall ? '1' : '2 / span 2',
                        }}
                    >
                        <FormControlLabel
                            value="UNI_DIRECTIONAL"
                            control={
                                <Radio
                                    sx={{
                                        color: '#414958',
                                        '&.Mui-checked': { color: '#20A8E0' },
                                    }}
                                />
                            }
                            label={t("One Way Route")}
                            sx={{
                                justifyContent: 'flex-start', // Left alignment
                                marginLeft: 0,
                                '& .MuiFormControlLabel-label': {
                                    fontFamily: 'Roboto',
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '18.75px',
                                    letterSpacing: 0,
                                    color: routeType === "UNI_DIRECTIONAL" ? '#20A8E0' : '#414958',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                },
                            }}
                        />
                        <FormControlLabel
                            value="BI_DIRECTIONAL"
                            control={
                                <Radio
                                    sx={{
                                        color: '#414958',
                                        '&.Mui-checked': { color: '#20A8E0' },
                                    }}
                                />
                            }
                            label={t("Round Route")}
                            sx={{
                                justifyContent: 'flex-start', // Left alignment
                                marginLeft: 0,
                                '& .MuiFormControlLabel-label': {
                                    fontFamily: 'Roboto',
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '18.75px',
                                    letterSpacing: 0,
                                    color: routeType === "BI_DIRECTIONAL" ? '#20A8E0' : '#414958',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                },
                            }}
                        />
                    </RadioGroup>
                </Box>
            </Paper>

            {/* Button Section - Only show in update mode */}
            {isUpdateMode && (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{
                        width: { xs: '100%', md: 'auto' },
                    }}
                >
                    {/* Only show Route Schedules button if user has permission */}
                    {canViewSchedules && (
                        <Button
                            variant="contained"
                            onClick={onRouteSchedulesClick} sx={{
                                width: { xs: '100%', sm: '154px' },
                                height: "40px",
                                borderRadius: "8px",
                                padding: "10px 20px",
                                border: '2px solid #20A8E0',
                                color: '#20A8E0',
                                fontFamily: 'Roboto',
                                fontWeight: 600,
                                lineHeight: '100%',
                                letterSpacing: 0,
                                backgroundColor: 'white',
                                textTransform: 'none',
                                "&:hover": { backgroundColor: "#F0F0F0" },
                            }}
                        >
                            {t("Route Schedules")}
                        </Button>
                    )}

                    {/* Only show Assign Schedules button if user has permission */}
                    {canAssignSchedules && (
                        <Button
                            variant="contained"
                            onClick={onAssignSchedulesClick}
                            sx={{
                                width: { xs: '100%', sm: '154px' },
                                height: "40px",
                                borderRadius: "8px",
                                padding: "10px 20px",
                                backgroundColor: "#20A8E0",
                                color: "#FFFFFF",
                                fontFamily: "Roboto",
                                fontWeight: 600,
                                lineHeight: "23.44px",
                                textTransform: "none",
                                whiteSpace: "nowrap",
                                "&:hover": { backgroundColor: "#1A90C8" },
                            }}
                        >
                            {t("Assign Schedules")}
                        </Button>
                    )}
                </Stack>
            )}
        </Box>
    );
}